import React, { useMemo, useState } from "react";
import { FaArrowLeft, FaCalendarAlt, FaImages, FaPen, FaPlus, FaTimes, FaTrashAlt } from "react-icons/fa";
import { Badge, ErrorBox, Field, Options } from "./common";
import { MediaPicker, MediaThumb } from "./MediaGallery";
import { errorMessage, fmtDate, fmtMoney, locationsToText, nullIfBlank, numOrNull, platformLabel } from "../dmApi";

const PLATFORMS_BY_CHANNEL = {
  SEM: ["Google Ads", "YouTube"],
  SMM: ["Meta", "WhatsApp Business", "LinkedIn", "YouTube"],
};
const PLACEMENTS = {
  Meta: ["Facebook", "Instagram", "Messenger", "Audience Network"],
  "Google Ads": ["Search", "Display", "Maps"],
  YouTube: ["In-stream", "Shorts", "Discovery"],
};
const MAX_AD_MEDIA = 10;

const blankToEmpty = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, v ?? ""]));
/** Ad-account platform whose campaigns a DM campaign can be linked to. */
const syncPlatform = (platform) => (platform === "Meta" ? "Meta" : platform === "Google Ads" || platform === "YouTube" ? "Google Ads" : null);

/** Pick the campaign in the client's connected ad account; sets the platform campaign id. */
function PlatformCampaignPicker({ workApi, tenantId, platform, value, onPick }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const target = syncPlatform(platform);
  if (!workApi || !target) return null;

  const load = async () => {
    setError("");
    try {
      const res = await workApi.platformCampaigns({ platform: target, ...(workApi.isStaff ? { tenant_id: tenantId } : {}) });
      setRows(res?.campaigns || []);
      if (res?.errors?.length) setError(res.errors[0]);
      if (!res?.campaigns?.length && !res?.errors?.length) {
        setError(
          workApi.isStaff
            ? `No ${target} campaigns found. Connect the ad account on the API Connections page.`
            : `No ${target} campaigns found. Ask Cloud Office to connect your ad account.`
        );
      }
    } catch (err) {
      setError(errorMessage(err, "Could not load campaigns"));
    }
  };

  return (
    <div style={{ marginTop: 4 }}>
      {rows ? (
        <select
          value={value || ""}
          onChange={(e) => {
            const row = rows.find((r) => String(r.id) === e.target.value);
            onPick(row || null);
          }}
          aria-label={`Link to a ${target} campaign`}
        >
          <option value="">Not linked</option>
          {rows.map((r) => (
            <option key={`${r.account_id}-${r.id}`} value={r.id}>
              {r.name} ({r.account}){r.linked_to && String(r.id) !== String(value) ? ` · linked to ${r.linked_to}` : ""}
            </option>
          ))}
        </select>
      ) : (
        <button type="button" className="dmp-link" onClick={load}>
          Pick from {target} ad account
        </button>
      )}
      {error ? <div className="dmp-muted" style={{ color: "var(--dmp-danger)", fontSize: 12 }}>{error}</div> : null}
    </div>
  );
}
/** Google Ads / YouTube call the middle level an "ad group"; Meta and the rest an "ad set". */
const setLabel = (platform) => (platform === "Google Ads" || platform === "YouTube" ? "Ad group" : "Ad set");

function CampaignForm({ meta, channels, initial, onSubmit, onCancel, saving, workApi, tenantId }) {
  const [form, setForm] = useState(() => ({
    Campaign_Name: "",
    Channel: channels[0] || "SMM",
    Platform: PLATFORMS_BY_CHANNEL[channels[0] || "SMM"][0],
    Goal: "Leads",
    Budget_Per_Day: "",
    Start_Date: "",
    End_Date: "",
    Status: "Draft",
    External_Id: "",
    Notes: "",
    ...blankToEmpty(initial),
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setChannel = (e) => {
    const channel = e.target.value;
    setForm((p) => ({ ...p, Channel: channel, Platform: PLATFORMS_BY_CHANNEL[channel][0] }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Campaign_Name: form.Campaign_Name.trim(),
      Channel: form.Channel,
      Platform: form.Platform,
      Goal: form.Goal,
      Budget_Per_Day: numOrNull(form.Budget_Per_Day),
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      Status: form.Status,
      External_Id: nullIfBlank(form.External_Id),
      Notes: nullIfBlank(form.Notes),
    });
  };

  return (
    <form onSubmit={submit} className="dmp-card">
      <div className="dmp-form-grid dmp-form-grid--4">
        <Field label="Campaign name *" wide>
          <input required value={form.Campaign_Name} onChange={set("Campaign_Name")} placeholder="TheRidge-Cmpn1" />
        </Field>
        <Field label="Channel">
          <select value={form.Channel} onChange={setChannel}>
            <Options values={channels} />
          </select>
        </Field>
        <Field label="Platform">
          <select value={form.Platform} onChange={set("Platform")}>
            <Options values={PLATFORMS_BY_CHANNEL[form.Channel] || meta?.ad_platforms} format={platformLabel} />
          </select>
        </Field>

        <Field label="Goal">
          <select value={form.Goal} onChange={set("Goal")}>
            <Options values={meta?.goals} />
          </select>
        </Field>
        <Field label="Budget per day (₹)">
          <input type="number" min={0} step="0.01" value={form.Budget_Per_Day} onChange={set("Budget_Per_Day")} />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>

        <Field label="Status">
          <select value={form.Status} onChange={set("Status")}>
            <Options values={meta?.run_statuses} />
          </select>
        </Field>
        <Field label="Campaign ID on platform">
          <input value={form.External_Id} onChange={set("External_Id")} />
          <PlatformCampaignPicker
            workApi={workApi}
            tenantId={tenantId}
            platform={form.Platform}
            value={form.External_Id}
            onPick={(row) =>
              setForm((p) => ({
                ...p,
                External_Id: row ? String(row.id) : "",
                Campaign_Name: p.Campaign_Name || (row ? row.name : ""),
              }))
            }
          />
        </Field>
        <Field label="Notes (audience, SEO/SEM keywords…)" wide>
          <textarea rows={1} value={form.Notes} onChange={set("Notes")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save campaign"}
        </button>
      </div>
    </form>
  );
}

/** Ad set (Meta) / ad group (Google): audience, placements, budget and dates shared by its ads. */
function AdSetForm({ meta, campaign, initial, onSubmit, onCancel, saving }) {
  const label = setLabel(campaign.Platform);
  const [form, setForm] = useState(() => ({
    Ad_Set_Name: "",
    Age_Min: "",
    Age_Max: "",
    Gender: "All",
    Budget_Per_Day: "",
    Start_Date: "",
    End_Date: "",
    External_Id: "",
    Status: "Draft",
    ...blankToEmpty(initial),
    Placements: initial?.Placements || [],
    Locations: locationsToText(initial?.Locations),
  }));
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const togglePlacement = (value) =>
    setForm((p) => ({
      ...p,
      Placements: p.Placements.includes(value) ? p.Placements.filter((v) => v !== value) : [...p.Placements, value],
    }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Ad_Set_Name: form.Ad_Set_Name.trim(),
      Placements: form.Placements,
      Locations: form.Locations,
      Age_Min: numOrNull(form.Age_Min),
      Age_Max: numOrNull(form.Age_Max),
      Gender: form.Gender,
      Budget_Per_Day: numOrNull(form.Budget_Per_Day),
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      External_Id: nullIfBlank(form.External_Id),
      Status: form.Status,
    });
  };

  const placementOptions = PLACEMENTS[campaign.Platform] || [];
  return (
    <form onSubmit={submit} className="dmp-card" style={{ background: "var(--dmp-soft)" }}>
      <div className="dmp-form-grid dmp-form-grid--4">
        <Field label={`${label} name *`} wide>
          <input required value={form.Ad_Set_Name} onChange={set("Ad_Set_Name")} placeholder="TheRidge-Adset1" />
        </Field>
        <Field label="Status">
          <select value={form.Status} onChange={set("Status")}>
            <Options values={meta?.run_statuses} />
          </select>
        </Field>
        <Field label={`${label} ID on platform`}>
          <input value={form.External_Id} onChange={set("External_Id")} />
        </Field>

        <Field label="Age min">
          <input type="number" min={13} max={65} value={form.Age_Min} onChange={set("Age_Min")} />
        </Field>
        <Field label="Age max">
          <input type="number" min={13} max={65} value={form.Age_Max} onChange={set("Age_Max")} />
        </Field>
        <Field label="Gender">
          <select value={form.Gender} onChange={set("Gender")}>
            <Options values={meta?.genders} />
          </select>
        </Field>
        <Field label="Budget per day (₹)">
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.Budget_Per_Day}
            onChange={set("Budget_Per_Day")}
            placeholder={campaign.Budget_Per_Day != null ? `Campaign: ${campaign.Budget_Per_Day}` : ""}
          />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>
        {placementOptions.length ? (
          <Field label="Placements" wide>
            <div className="dmp-check-row">
              {placementOptions.map((p) => (
                <label key={p}>
                  <input type="checkbox" checked={form.Placements.includes(p)} onChange={() => togglePlacement(p)} />
                  {p}
                </label>
              ))}
            </div>
          </Field>
        ) : null}

        <Field label="Locations (comma separated, radius e.g. LB Nagar (R2km))" full>
          <input value={form.Locations} onChange={set("Locations")} placeholder="Dhandumailaram (R45km), Vijayawada (R45km)" />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : `Save ${label.toLowerCase()}`}
        </button>
      </div>
    </form>
  );
}

/** Media library files used by an ad, in order (first = main image; several = carousel). */
function AdMediaField({ api, projectId, value, onChange }) {
  const [picking, setPicking] = useState(false);
  const remove = (id) => onChange(value.filter((m) => m.Media_Id !== id));
  const moveFirst = (index) => {
    const next = [...value];
    const [item] = next.splice(index, 1);
    next.splice(index - 1, 0, item);
    onChange(next);
  };

  return (
    <div className="dmp-ad-media-field">
      {value.length ? (
        <div className="dmp-ad-media">
          {value.map((m, index) => (
            <div key={m.Media_Id} className="dmp-ad-media__item" title={m.Original_Name || ""}>
              <MediaThumb media={m} />
              <span className="dmp-ad-media__order">{index + 1}</span>
              <span className="dmp-ad-media__tools">
                {index > 0 ? (
                  <button type="button" aria-label="Move earlier" title="Move earlier" onClick={() => moveFirst(index)}>
                    <FaArrowLeft />
                  </button>
                ) : null}
                <button type="button" aria-label="Remove" title="Remove" onClick={() => remove(m.Media_Id)}>
                  <FaTimes />
                </button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="dmp-muted">No media yet. Add posters, images or videos from the Media Library.</div>
      )}
      {value.length < MAX_AD_MEDIA ? (
        <button type="button" className="dmp-chip-btn" onClick={() => setPicking((v) => !v)}>
          <FaImages aria-hidden="true" /> {picking ? "Close library" : "Add from Media Library"}
        </button>
      ) : null}
      {picking ? (
        <MediaPicker
          api={api}
          projectId={projectId}
          title="Media Library"
          excludeIds={value.map((m) => m.Media_Id)}
          onClose={() => setPicking(false)}
          onPick={(_url, media) => {
            onChange([...value, media]);
            if (value.length + 1 >= MAX_AD_MEDIA) setPicking(false);
          }}
        />
      ) : null}
    </div>
  );
}

function AdForm({ api, projectId, meta, approved, initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    Ad_Name: "",
    Preview_URL: "",
    External_Id: "",
    Status: "Draft",
    ...blankToEmpty(initial),
    Content_Id: initial?.Content_Id ? String(initial.Content_Id) : "",
  }));
  const [media, setMedia] = useState(() => initial?.Media || []);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      Ad_Name: form.Ad_Name.trim(),
      Content_Id: numOrNull(form.Content_Id),
      Media_Ids: media.map((m) => m.Media_Id),
      Preview_URL: nullIfBlank(form.Preview_URL),
      External_Id: nullIfBlank(form.External_Id),
      Status: form.Status,
    });
  };

  return (
    <form onSubmit={submit} className="dmp-card" style={{ background: "var(--dmp-soft)" }}>
      <div className="dmp-form-grid dmp-form-grid--4">
        <Field label="Ad name *" wide>
          <input required value={form.Ad_Name} onChange={set("Ad_Name")} placeholder="TheRidge-Ad1" />
        </Field>
        <Field label="Status">
          <select value={form.Status} onChange={set("Status")}>
            <Options values={meta?.run_statuses} />
          </select>
        </Field>
        <Field label="Ad ID on platform">
          <input value={form.External_Id} onChange={set("External_Id")} />
        </Field>

        <Field label="Ad content (approved)" wide>
          <select value={form.Content_Id} onChange={set("Content_Id")}>
            <option value="">None</option>
            {approved.map((c) => (
              <option key={c.Content_Id} value={c.Content_Id}>
                {c.Content_Type}: {c.Title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ad preview link" wide>
          <input value={form.Preview_URL} onChange={set("Preview_URL")} placeholder="https://fb.me/…" />
        </Field>

        <Field label={`Media (${media.length}/${MAX_AD_MEDIA}) — first is the main image; several make a carousel`} full>
          <AdMediaField api={api} projectId={projectId} value={media} onChange={setMedia} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : "Save ad"}
        </button>
      </div>
    </form>
  );
}

function audienceText(s) {
  const ages = s.Age_Min || s.Age_Max ? `${s.Age_Min || 13}–${s.Age_Max || 65}` : "All ages";
  return s.Gender && s.Gender !== "All" ? `${ages} · ${s.Gender}` : ages;
}

/** Paid campaigns (SEM: Google Ads / SMM: Meta Ads): campaign → ad sets / ad groups → ads with media. */
function CampaignTable({ api, meta, projectId, campaigns, content, access, onChange, workApi, tenantId }) {
  // {type: 'campaign'|'adset'|'ad', campaign?, adSet?, item?}
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const approved = useMemo(() => content.filter((c) => c.Review_Status === "Approved"), [content]);
  const channels = (meta?.channels || ["SEM", "SMM"]).filter((c) => (access?.services || []).includes(c));
  const contentTitle = (id) => content.find((c) => c.Content_Id === id)?.Title;

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      await onChange();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    }
  };

  const save = async (fn) => {
    setSaving(true);
    const ok = await run(fn);
    setSaving(false);
    if (ok) setEditing(null);
  };

  const saveCampaign = (body) =>
    save(() => (editing.item ? api.updateCampaign(editing.item.Campaign_Id, body) : api.createCampaign(projectId, body)));
  const saveAdSet = (body) =>
    save(() =>
      editing.item ? api.updateAdSet(editing.item.Ad_Set_Id, body) : api.createAdSet(editing.campaign.Campaign_Id, body)
    );
  const saveAd = (body) =>
    save(() => (editing.item ? api.updateAd(editing.item.Ad_Id, body) : api.createAd(editing.adSet.Ad_Set_Id, body)));

  const creatingCampaign = editing?.type === "campaign" && !editing.item;
  const isEditing = (type, key, id) => editing?.type === type && (editing.item?.[key] ?? null) === id;
  const addingAdTo = (adSetId) => editing?.type === "ad" && !editing.item && editing.adSet?.Ad_Set_Id === adSetId;

  const adForm = (item) => (
    <AdForm
      api={api}
      projectId={projectId}
      meta={meta}
      approved={approved}
      initial={item}
      saving={saving}
      onSubmit={saveAd}
      onCancel={() => setEditing(null)}
    />
  );

  const renderAds = (c, s) => {
    const ads = s.Ads || [];
    if (!ads.length) return addingAdTo(s.Ad_Set_Id) ? null : <div className="dmp-campaign__empty">No ads yet.</div>;
    return (
      <div className="dmp-table-wrap">
        <table className="dmp-table dmp-ads-table">
          <thead>
            <tr>
              <th>Ad</th>
              <th>Media</th>
              <th>Content</th>
              <th>Preview</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ads.map((ad) =>
              isEditing("ad", "Ad_Id", ad.Ad_Id) ? (
                <tr key={ad.Ad_Id} className="dmp-ads-table__form-row">
                  <td colSpan={6}>{adForm(ad)}</td>
                </tr>
              ) : (
                <tr key={ad.Ad_Id}>
                  <td className="dmp-ads-table__name">
                    <strong>{ad.Ad_Name}</strong>
                    {ad.External_Id ? <div className="dmp-muted">{ad.External_Id}</div> : null}
                  </td>
                  <td data-label="Media">
                    {(ad.Media || []).length ? (
                      <div className="dmp-ad-media dmp-ad-media--sm">
                        {ad.Media.slice(0, 4).map((m) => (
                          <span key={m.Media_Id} className="dmp-ad-media__item" title={m.Original_Name || ""}>
                            <MediaThumb media={m} />
                          </span>
                        ))}
                        {ad.Media.length > 4 ? <span className="dmp-ad-media__more">+{ad.Media.length - 4}</span> : null}
                      </div>
                    ) : (
                      <span className="dmp-muted">—</span>
                    )}
                  </td>
                  <td data-label="Content">{contentTitle(ad.Content_Id) || <span className="dmp-muted">—</span>}</td>
                  <td data-label="Preview">
                    {ad.Preview_URL ? (
                      <a href={ad.Preview_URL} target="_blank" rel="noreferrer">
                        Open
                      </a>
                    ) : (
                      <span className="dmp-muted">—</span>
                    )}
                  </td>
                  <td data-label="Status">
                    <Badge value={ad.Status} />
                  </td>
                  <td className="dmp-ads-table__actions">
                    <div className="dmp-item__actions" style={{ marginTop: 0 }}>
                      <button type="button" className="dmp-link" onClick={() => setEditing({ type: "ad", campaign: c, adSet: s, item: ad })}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="dmp-link"
                        style={{ color: "var(--dmp-danger)" }}
                        onClick={() => window.confirm(`Delete ad "${ad.Ad_Name}"?`) && run(() => api.deleteAd(ad.Ad_Id))}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderAdSet = (c, s) => {
    const label = setLabel(c.Platform);
    if (isEditing("adset", "Ad_Set_Id", s.Ad_Set_Id)) {
      return (
        <div key={s.Ad_Set_Id} className="dmp-adset">
          <AdSetForm meta={meta} campaign={c} initial={s} saving={saving} onSubmit={saveAdSet} onCancel={() => setEditing(null)} />
        </div>
      );
    }
    const meta_ = [
      audienceText(s),
      s.Budget_Per_Day != null ? `${fmtMoney(s.Budget_Per_Day)}/day` : null,
      s.Start_Date || s.End_Date ? `${fmtDate(s.Start_Date)} → ${fmtDate(s.End_Date)}` : null,
      (s.Placements || []).length ? s.Placements.join(", ") : null,
      locationsToText(s.Locations) || null,
    ].filter(Boolean);
    return (
      <div key={s.Ad_Set_Id} className="dmp-adset">
        <div className="dmp-adset__head">
          <div className="dmp-adset__title">
            <span className="dmp-citem__type">{label}</span>
            <strong>{s.Ad_Set_Name}</strong>
            <Badge value={s.Status} />
          </div>
          <div className="dmp-citem__icons">
            <button
              type="button"
              className="dmp-citem__icon"
              title={`Edit ${label.toLowerCase()}`}
              aria-label={`Edit ${label.toLowerCase()}`}
              onClick={() => setEditing({ type: "adset", campaign: c, item: s })}
            >
              <FaPen />
            </button>
            <button
              type="button"
              className="dmp-citem__icon dmp-citem__icon--danger"
              title={`Delete ${label.toLowerCase()}`}
              aria-label={`Delete ${label.toLowerCase()}`}
              onClick={() =>
                window.confirm(`Delete ${label.toLowerCase()} "${s.Ad_Set_Name}" and its ads?`) && run(() => api.deleteAdSet(s.Ad_Set_Id))
              }
            >
              <FaTrashAlt />
            </button>
          </div>
        </div>
        {meta_.length ? <div className="dmp-adset__meta">{meta_.join(" · ")}</div> : null}
        <div className="dmp-campaign__ads-head">
          <span>
            Ads <span className="dmp-board__count">{(s.Ads || []).length}</span>
          </span>
          <button type="button" className="dmp-chip-btn" onClick={() => setEditing({ type: "ad", campaign: c, adSet: s })}>
            <FaPlus aria-hidden="true" />
            Add ad
          </button>
        </div>
        {addingAdTo(s.Ad_Set_Id) ? adForm(null) : null}
        {renderAds(c, s)}
      </div>
    );
  };

  return (
    <>
      {creatingCampaign ? null : (
        <div className="dmp-header dmp-header--section" style={{ marginBottom: 10, justifyContent: "flex-end" }}>
          <button type="button" className="dmp-btn" onClick={() => setEditing({ type: "campaign" })} disabled={!channels.length}>
            New campaign
          </button>
        </div>
      )}
      <ErrorBox message={error} />
      {creatingCampaign ? (
        <CampaignForm
          meta={meta}
          channels={channels}
          saving={saving}
          onSubmit={saveCampaign}
          onCancel={() => setEditing(null)}
          workApi={workApi}
          tenantId={tenantId}
        />
      ) : null}

      {campaigns.length === 0 && !creatingCampaign ? (
        <div className="dmp-card dmp-empty">
          No campaigns yet. Create a campaign, then add ad sets (Meta) or ad groups (Google) inside it, and ads
          with their posters, images or videos from the Media Library.
        </div>
      ) : null}

      {campaigns.map((c) => {
        const adSets = c.Ad_Sets || [];
        const label = setLabel(c.Platform);
        return (
          <div key={c.Campaign_Id} className="dmp-campaign">
            <div className="dmp-campaign__head">
              <div className="dmp-campaign__top">
                <div className="dmp-campaign__title">
                  <strong className="dmp-campaign__name">{c.Campaign_Name}</strong>
                  <Badge value={c.Status} />
                </div>
                <div className="dmp-citem__icons">
                  <button
                    type="button"
                    className="dmp-citem__icon"
                    title="Edit campaign"
                    aria-label="Edit campaign"
                    onClick={() => setEditing({ type: "campaign", item: c })}
                  >
                    <FaPen />
                  </button>
                  <button
                    type="button"
                    className="dmp-citem__icon dmp-citem__icon--danger"
                    title="Delete campaign"
                    aria-label="Delete campaign"
                    onClick={() =>
                      window.confirm(`Delete campaign "${c.Campaign_Name}" with its ${label.toLowerCase()}s and ads?`) &&
                      run(() => api.deleteCampaign(c.Campaign_Id))
                    }
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              </div>
              <div className="dmp-campaign__channel">
                <span className="dmp-citem__type">{c.Channel}</span>
                <span>{platformLabel(c.Platform)}</span>
              </div>
              <dl className="dmp-campaign__stats">
                <div>
                  <dt>Goal</dt>
                  <dd>{c.Goal || "—"}</dd>
                </div>
                <div>
                  <dt>Budget / day</dt>
                  <dd>{fmtMoney(c.Budget_Per_Day)}</dd>
                </div>
                <div>
                  <dt>CRM leads</dt>
                  <dd>{c.Crm_Leads ?? 0}</dd>
                </div>
              </dl>
              <div className="dmp-campaign__dates">
                <span>
                  <FaCalendarAlt aria-hidden="true" />
                  {fmtDate(c.Start_Date)} → {fmtDate(c.End_Date)}
                </span>
                {c.Expires_On ? <span>Expires {fmtDate(c.Expires_On)}</span> : null}
                {c.Published_On ? (
                  <span>
                    Published {fmtDate(c.Published_On)}
                    {c.Published_By_Name ? ` by ${c.Published_By_Name}` : ""}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="dmp-campaign__body">
              {isEditing("campaign", "Campaign_Id", c.Campaign_Id) ? (
                <CampaignForm
                  meta={meta}
                  channels={channels.includes(c.Channel) ? channels : [c.Channel, ...channels]}
                  initial={c}
                  saving={saving}
                  onSubmit={saveCampaign}
                  onCancel={() => setEditing(null)}
                  workApi={workApi}
                  tenantId={tenantId}
                />
              ) : null}
              <div className="dmp-campaign__ads-head">
                <span>
                  {label}s <span className="dmp-board__count">{adSets.length}</span>
                </span>
                <button type="button" className="dmp-chip-btn" onClick={() => setEditing({ type: "adset", campaign: c })}>
                  <FaPlus aria-hidden="true" />
                  Add {label.toLowerCase()}
                </button>
              </div>
              {editing?.type === "adset" && !editing.item && editing.campaign?.Campaign_Id === c.Campaign_Id ? (
                <AdSetForm meta={meta} campaign={c} saving={saving} onSubmit={saveAdSet} onCancel={() => setEditing(null)} />
              ) : null}
              {adSets.length === 0 ? (
                <div className="dmp-campaign__empty">No {label.toLowerCase()}s yet. Add one, then add ads with their media.</div>
              ) : (
                adSets.map((s) => renderAdSet(c, s))
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default CampaignTable;
